import {
  Anchor,
  Button,
  Checkbox,
  Group,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useEffect, useState } from 'react';
import { PiX } from 'react-icons/pi';
import { Link } from 'wouter';
import Modal from '@/components/modal/Modal';
import { LocalContactPicker } from '@/pages/contacts/LocalContactPicker';
import type { Contact } from '@/models/contacts';
import { isTestContactEmail, TOffer, useCreateOffer } from '@/models/offers';

interface CreateOfferModalProps {
  opened: boolean;
  onClose: () => void;
  onCreated: (offer: TOffer) => void;
  initialContact?: Contact;
}

export default function CreateOfferModal({
  opened,
  onClose,
  onCreated,
  initialContact,
}: CreateOfferModalProps) {
  const [title, setTitle] = useState('');
  const [isTest, setIsTest] = useState(false);
  const [contact, setContact] = useState<Contact | null>(null);
  const createOffer = useCreateOffer();

  useEffect(() => {
    if (opened && initialContact) setContact(initialContact);
  }, [opened, initialContact]);

  const handleClear = () => setContact(null);

  const contactNoEmail = !!contact && !contact.email?.trim();
  const contactNotAllowed =
    isTest && !!contact?.email && !isTestContactEmail(contact.email);
  const blocked =
    !title.trim() || !contact || contactNoEmail || contactNotAllowed;

  const handleCreate = () => {
    if (blocked) return;

    createOffer.mutate(
      { title: title.trim(), contactId: contact.id, isTest },
      {
        onSuccess: (offer) => {
          onCreated(offer);
          setTitle('');
          setIsTest(false);
          handleClear();
        },
        onError: (e) => {
          notifications.show({
            color: 'red',
            title: 'Error',
            message: `Failed to create offer: ${e}`,
          });
        },
      },
    );
  };

  const handleClose = () => {
    onClose();
    setTitle('');
    setIsTest(false);
    handleClear();
  };

  const contactLabel = contact
    ? [
        [contact.firstName, contact.lastName].filter(Boolean).join(' '),
        contact.email,
      ]
        .filter(Boolean)
        .join(' - ') || `#${contact.id}`
    : null;

  return (
    <Modal opened={opened} onClose={handleClose} title="Create Offer">
      <Stack gap="md" p="md">
        <TextInput
          label="Title"
          placeholder="Enter offer title"
          value={title}
          onChange={(e) => setTitle(e.currentTarget.value)}
          autoFocus
        />

        {contact ? (
          <Group gap="xs">
            <Text fz="sm" fw={500}>
              {contactLabel}
            </Text>
            <Button
              size="compact-xs"
              variant="subtle"
              color="gray"
              onClick={handleClear}
            >
              <PiX size={14} />
            </Button>
          </Group>
        ) : (
          <LocalContactPicker
            offerable
            label="Contact"
            placeholder="Search local contacts..."
            onContactSelect={(c) => setContact(c)}
          />
        )}

        <Checkbox
          label="Test offer"
          description="For trying things out. It works like a real offer but is never meant for a client, and only a contact with an @legalbison.com email can receive it."
          checked={isTest}
          onChange={(e) => setIsTest(e.currentTarget.checked)}
        />

        {contactNoEmail && contact && (
          <Text fz="xs" c="red">
            This contact has no email, and an offer cannot be created without
            it. Add the email on the{' '}
            <Anchor component={Link} to={`~/contacts/${contact.id}`} fz="xs">
              contact card
            </Anchor>
            .
          </Text>
        )}

        {contactNotAllowed && (
          <Text fz="xs" c="red">
            A test offer needs a contact with an @legalbison.com email.
          </Text>
        )}

        <Group justify="flex-end">
          <Button variant="subtle" onClick={handleClose}>
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={blocked}
            loading={createOffer.isPending}
          >
            Create
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
